// src/LeadDetailPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  fetchLeadById,
  updateLead,
  deleteLead,
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,
  sendEmail,
} from "./api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const templates = {
  post_meeting: {
    subject: "📌 Riepilogo meeting e funzionalità",
    body: `
      <h1>Ciao {{nome}},</h1>
      <p>Grazie per il meeting! Ecco il riepilogo delle funzionalità viste:</p>
      <ul>
        <li>✅ Gestione Lead</li>
        <li>✅ Integrazione Calendly</li>
        <li>✅ Automazioni Zapier</li>
      </ul>
      <p>👉 Link utile: <a href="{{link}}">{{link}}</a></p>
      <hr>
      <p>
        Cordiali saluti,<br>
        <strong>Il team Labelfy</strong>
      </p>
      <p>
        <img src="https://labelfy.it/wp-content/uploads/2023/09/logo_labelfy.png" alt="Labelfy Logo" width="150" style="margin-top:10px;">
      </p>
    `,
  },
  registration: {
    subject: "🚀 Completa la tua registrazione su Labelfy",
    body: `
      <h1>Benvenuto {{nome}}!</h1>
      <p>Per completare la tua registrazione, clicca qui:</p>
      <p><a href="{{link}}">{{link}}</a></p>
      <hr>
      <p style="font-size:14px; color:#555;">
        Cordiali saluti,<br>
        <strong>Il team Labelfy</strong>
      </p>
      <p>
        <img src="https://labelfy.it/wp-content/uploads/2023/09/logo_labelfy.png" alt="Labelfy Logo" width="150" style="margin-top:10px;">
      </p>
    `,
  },
  support_call: {
    subject: "🛠️ Assistenza tecnica: prenota la tua chiamata",
    body: `
      <h1>Ciao {{nome}},</h1>
      <p>Hai bisogno di supporto tecnico? Prenota una chiamata con noi:</p>
      <p><a href="{{link}}">{{link}}</a></p>
      <hr>
      <p style="font-size:14px; color:#555;">
        Cordiali saluti,<br>
        <strong>Il team Labelfy</strong>
      </p>
      <p>
        <img src="https://labelfy.it/wp-content/uploads/2023/09/logo_labelfy.png" alt="Labelfy Logo" width="150" style="margin-top:10px;">
      </p>
    `,
  },
};

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [dueDate, setDueDate] = useState("");

  // Stato email
  const [template, setTemplate] = useState("post_meeting");
  const [subject, setSubject] = useState(templates.post_meeting.subject);
  const [content, setContent] = useState(templates.post_meeting.body);

  useEffect(() => {
    loadLead();
    loadTasks();
  }, [id]);

  const loadLead = async () => {
    try {
      const data = await fetchLeadById(id);
      setLead(data);
    } catch (err) {
      console.error("Errore caricamento lead:", err);
    }
  };

  const loadTasks = async () => {
    try {
      const data = await fetchTasks();
      setTasks(data.filter((t) => String(t.lead_id) === String(id)));
    } catch (err) {
      console.error("Errore caricamento tasks:", err);
    }
  };

  const handleUpdate = async () => {
    try {
      const updated = await updateLead(id, lead);
      setLead(updated);
      alert("✅ Lead aggiornato");
    } catch (err) {
      console.error(err);
      alert("❌ Errore aggiornamento lead");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Eliminare questo lead?")) return;
    try {
      await deleteLead(id);
      alert("Lead eliminato");
      navigate("/");
    } catch (err) {
      console.error(err);
    }
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    try {
      const saved = await createTask({
        text: newTask.trim(),
        completed: false,
        due_datetime: dueDate || null,
        lead_id: Number(id),
      });
      setTasks([...tasks, saved]);
      setNewTask("");
      setDueDate("");
    } catch (err) {
      console.error(err);
    }
  };

  const toggleTask = async (task) => {
    try {
      const updated = await updateTask(task.id, {
        ...task,
        completed: !task.completed,
      });
      setTasks(tasks.map((t) => (t.id === task.id ? updated : t)));
    } catch (err) {
      console.error(err);
    }
  };

  const removeTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      setTasks(tasks.filter((t) => t.id !== taskId));
    } catch (err) {
      console.error(err);
    }
  };

  const handleTemplateChange = (e) => {
    const selected = e.target.value;
    setTemplate(selected);

    // Recupero soggetto e corpo
    const tpl = templates[selected];
    setSubject(tpl.subject);

    // 🔄 Sostituisco subito i placeholder con i valori del lead
    let filledBody = tpl.body
      .replace(/{{nome}}/g, lead?.nome || "")
      .replace(/{{link}}/g, "https://my.labelfy.it/login");

    setContent(filledBody);
  };


  const handleSendEmail = async () => {
    try {
      await sendEmail({
        lead_email: lead.email,
        lead_name: lead.nome,
        template,
        subject,
        content,
        placeholders: {
          nome: lead.nome,
          link: "https://crm.labelfy.it/register",
        },
      });
      alert("✅ Email inviata");
    } catch (err) {
      console.error(err);
      alert("❌ Errore invio email");
    }
  };

  if (!lead) return <div className="p-4">⏳ Caricamento…</div>;

  return (
    <div className="p-4 space-y-6">
      <Link to="/" className="text-blue-600 underline">← Torna ai lead</Link>
      <h1 className="text-2xl font-bold">Lead: {lead.nome}</h1>

      {/* Info Lead */}
      <div className="bg-white p-4 rounded shadow space-y-2">
        <input
          className="border p-2 w-full"
          value={lead.nome || ""}
          onChange={(e) => setLead({ ...lead, nome: e.target.value })}
        />
        <input
          className="border p-2 w-full"
          value={lead.email || ""}
          onChange={(e) => setLead({ ...lead, email: e.target.value })}
        />
        <input
          className="border p-2 w-full"
          value={lead.telefono || ""}
          onChange={(e) => setLead({ ...lead, telefono: e.target.value })}
        />
        <textarea
          className="border p-2 w-full"
          value={lead.note || ""}
          onChange={(e) => setLead({ ...lead, note: e.target.value })}
        />
        <button onClick={handleUpdate} className="bg-blue-500 text-white px-4 py-2 rounded">
          💾 Salva
        </button>
        <button onClick={handleDelete} className="bg-red-500 text-white px-4 py-2 rounded ml-2">
          🗑️ Elimina
        </button>
      </div>

      {/* 📧 Invia Email */}
      <div className="bg-white p-4 rounded shadow">
        <h2 className="font-bold mb-2">📧 Invia Email</h2>
        <div className="space-y-3">
          <select
            value={template}
            onChange={handleTemplateChange}
            className="border p-2 rounded w-full"
          >
            <option value="post_meeting">Post Meeting</option>
            <option value="registration">Registrazione</option>
            <option value="support_call">Assistenza Tecnica</option>
          </select>

          <input
            type="text"
            className="border p-2 w-full"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            className="bg-white"
          />

          <button
            onClick={handleSendEmail}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            📩 Invia Email
          </button>
        </div>
      </div>

      {/* Storico modifiche */}
      <div className="bg-white p-4 rounded shadow">
        <h2 className="font-bold mb-2">📜 Storico modifiche</h2>
        {lead.storico_modifiche?.length ? (
          <ul className="list-disc pl-5">
            {lead.storico_modifiche.map((s, i) => (
              <li key={i} className="text-sm">
                <strong>{s.timestamp}</strong> – {s.azione} ({s.utente})
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">Nessuna modifica</p>
        )}
      </div>

      {/* To Do */}
      <div className="bg-white p-4 rounded shadow">
        <h2 className="font-bold mb-2">📝 To-Do per questo lead</h2>
        <div className="flex gap-2 mb-4">
          <input
            className="border p-2 flex-1"
            placeholder="Nuovo task"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
          />
          <input
            type="datetime-local"
            className="border p-2"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <button onClick={addTask} className="bg-green-500 text-white px-3 rounded">
            ➕
          </button>
        </div>
        {tasks.length ? (
          <ul className="space-y-2">
            {tasks.map((t) => (
              <li key={t.id} className="flex justify-between items-center p-2 border rounded">
                <div>
                  <p className={t.completed ? "line-through" : ""}>{t.text}</p>
                  {t.due_datetime && (
                    <span className="text-xs text-gray-500">
                      🕒 {new Date(t.due_datetime).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleTask(t)}
                    className="bg-blue-400 text-white px-2 rounded"
                  >
                    {t.completed ? "↩️" : "✅"}
                  </button>
                  <button
                    onClick={() => removeTask(t.id)}
                    className="bg-red-400 text-white px-2 rounded"
                  >
                    ❌
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">Nessun task</p>
        )}
      </div>
    </div>
  );
};

export default LeadDetailPage;
